import {
  Component,
  EventEmitter,
  Input,
  OnDestroy,
  OnInit,
  Output,
  ViewChild,
} from '@angular/core';
import { ActivatedRoute, ParamMap, Router } from '@angular/router';
import {
  ColumnMenuSettings,
  DataStateChangeEvent,
  GridComponent as KendoGrid,
  GridDataResult,
} from '@progress/kendo-angular-grid';
import { State } from '@progress/kendo-data-query';
import {
  SVGIcon,
  cloudIcon,
  arrowRotateCcwIcon,
  plusIcon,
  xIcon,
} from '@progress/kendo-svg-icons';
import {
  find,
  first,
  get,
  groupBy,
  includes,
  keys,
  orderBy,
  some,
  trim,
} from 'lodash';
import { Observable, Subject, filter, map, take, takeUntil } from 'rxjs';
import {
  DOHMH_NOT_SUPER_USER_IDS,
  DOHMH_ROLES,
  DOHMH_ROLE_IDS,
} from 'src/app/modules/ei-transition/constants/dohmh-roles';
import { ASSESSMENT_INFO } from 'src/app/modules/evaluations/assessment/constants/assessment-routes';
import { DocumentService } from 'src/app/services/document.service';
import { PdfAndImageService } from 'src/app/services/pdf-image.service';
import { RoutesConstantService } from 'src/app/services/routes-constant.service';
import { StudentService } from 'src/app/services/student.service';
import { AuthStore } from 'src/app/stores/auth.store';
import { StudentStore } from 'src/app/stores/student.store';
import { ColumnType } from '../../enums/column-type';
import { Permissions } from '../../enums/permissions';
import { getReferenceDataById } from '../../functions/get-reference-data-by-id';
import { ColumnComponentInputs } from '../../interfaces/column-component-inputs';
import { LFDocument } from '../../interfaces/document';
import { EvaluationNestedRecord } from '../../interfaces/evaluation-record';
import { IReferenceData } from '../../interfaces/form/reference-data.model';
import {
  GridDataResponse,
  NestedGridRequest,
} from '../../interfaces/grid-data-response';
import { Permission } from '../../models/permission';
import { User } from '../../models/user';
import { GridService } from '../../services/grid.service';
import { StudentDocument } from '../../interfaces/student-document';
import { DropDownFilterSettings } from '@progress/kendo-angular-dropdowns';
import { referenceDataFilterSettings } from '../../constants/reference-data-filter-settings';
import { Location } from '../../models/location';
import { viewAction } from '../../constants/form-controls';
import { Grid } from '../../interfaces/grid';
import { MenuAction } from '../../enums/menu-items';
import {
  AGENCY_NAME,
  DOCUMENT_NAME,
  EITRANSITION,
  EVENT_LOG,
  FIRST_INDEX,
  LOCATION_CODE,
  LOCATION_KEY_INDEX,
  LOCATION_LENGTH,
  LOG_EVENT,
  RE_UPLOAD,
  SOCIAL_HISTORY_EVALUATION,
  SOCIAL_HISTORY_UPDATE,
  STUDENT_NAME,
  USER_ROLE_CSE_SCHOOL_PSYCHOLOGIST,
} from '../../constants/grid-actions';

@Component({
    selector: 'app-grid',
    templateUrl: './grid.component.html',
    styleUrls: ['./grid.component.scss'],
    standalone: false
})
export class GridComponent implements OnInit, OnDestroy {
  @Input() columnDefinitions!: ColumnComponentInputs[];
  @Input() expandedRowColumnDefinitions!: ColumnComponentInputs[];
  @Input() gridApiRoute: string;
  @Input() studentSk: string;
  @Input() gridNestedApiRoute: string;
  @Input() initialState: State;
  @Input() initialNesteState: State;
  @Input() addButtonText: string;
  @Input() permissionToView: number;
  @Input() refershGrid: boolean;
  @Input() referenceData: Record<string, IReferenceData[]>;
  @Output() addToGrid: EventEmitter<string> = new EventEmitter<string>();
  @ViewChild('grid') grid: KendoGrid;
  public refreshIcon: SVGIcon = arrowRotateCcwIcon;
  private ngUnsubscribe: Subject<void> = new Subject<void>();

  menuSettings: ColumnMenuSettings = {
    view: 'list',
    columnChooser: false,
  };
  loading: boolean = true;
  gridData: GridDataResponse;
  state: State;
  columnTypeEnum: typeof ColumnType = ColumnType;
  user: User;
  svgPlus: SVGIcon = plusIcon;
  xIcon: SVGIcon = xIcon;
  cloudIcon: SVGIcon = cloudIcon;
  nestedState: State;
  status: Grid[] = [
    {
      name: 'Open',
    },
    {
      name: 'Draft',
    },
  ]; // TODO: Change to use reference servic
  nestedGridData: any | EvaluationNestedRecord[] | LFDocument[] = {};

  getReferenceDataById: Function = getReferenceDataById.bind(this);
  userDetails: User;
  currentAgency?: Location;
  filterSettings: DropDownFilterSettings = referenceDataFilterSettings;
  canSelectAgency: boolean = false;
  viewMode: boolean = false;

  readonly eitransition: string = EITRANSITION;
  readonly eventLog: string = EVENT_LOG;
  readonly studentName: string = STUDENT_NAME;
  readonly locationCode: string = LOCATION_CODE;
  readonly documentName: string = DOCUMENT_NAME;
  readonly socialHistoryEvaluation: string = SOCIAL_HISTORY_EVALUATION;
  readonly socialHistoryUpdate: string = SOCIAL_HISTORY_UPDATE;

  constructor(
    private gridService: GridService,
    public studentService: StudentService,
    private authStore: AuthStore,
    private router: Router,
    private routesConstantService: RoutesConstantService,
    private studentStore: StudentStore,
    private pdfAndImageService: PdfAndImageService,
    private documentService: DocumentService,
    private route: ActivatedRoute
  ) {
    this.allData = this.allData.bind(this);
    this.authStore.UserInfo$.pipe(
      filter((user: User) => !!user),
      takeUntil(this.ngUnsubscribe)
    ).subscribe((userInfo: User) => {
      this.userDetails = userInfo;
    });
  }

  ngOnInit(): void {
    this.route.paramMap.subscribe((params: ParamMap) => {
      const action: string = params.get('action');
      if (action === viewAction) {
        this.viewMode = true;
      }
    });

    this.canSelectAgency = this.isEi();
    this.state = this.initialState;
    this.studentService.refreshGridSubject$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((data: boolean) => {
        if (data) {
          this.refreshGrid();
        }
      });
    this.authStore.userInfo$
      .pipe(takeUntil(this.ngUnsubscribe))
      .subscribe((userInfo: User) => {
        this.user = userInfo;
        this.user = this.authStore.UserInfo;
        this.nestedState = this.initialNesteState;
        if (this.isEi()) {
          if (
            this.authStore.UserInfo?.selectedRole?.roleId !== null &&
            !includes(
              DOHMH_ROLE_IDS,
              this.authStore.UserInfo?.selectedRole?.roleId
            )
          ) {
            this.sendRequest();
          }
        } else {
          this.sendRequest();
        }
      });
  }

  filteredAgency(): Location[] {
    let locations: Location[] = this.referenceData?.agency as Location[];

    if (!this.currentAgency) {
      if (
        this.referenceData?.agency.find(
          (data: IReferenceData) =>
            (data as Location).locationKey === LOCATION_KEY_INDEX
        )
      ) {
        this.currentAgency = this.referenceData?.agency.find(
          (data: IReferenceData) =>
            (data as Location).locationKey === LOCATION_KEY_INDEX
        ) as Location;
        this.sendRequest();
      } else if (
        includes(
          DOHMH_NOT_SUPER_USER_IDS,
          this.authStore.UserInfo?.selectedRole?.roleId
        )
      ) {
        locations = this.referenceData?.agency.filter(
          (agency: IReferenceData) =>
            this.authStore.UserInfo?.selectedRole?.locations.some(
              (location: Location) =>
                location.locationCode ===
                (agency as Location).locationKey.toString()
            )
        ) as Location[];
        if (locations?.length === LOCATION_LENGTH) {
          this.currentAgency = locations.at(FIRST_INDEX);
          this.columnDefinitions = this.columnDefinitions.filter(
            (definition: ColumnComponentInputs) =>
              definition.title !== AGENCY_NAME
          );
          this.sendRequest();
        }
      }
    } else if (
      includes(
        DOHMH_NOT_SUPER_USER_IDS,
        this.authStore.UserInfo?.selectedRole?.roleId
      )
    ) {
      locations = this.referenceData?.agency.filter((agency: IReferenceData) =>
        this.authStore.UserInfo?.selectedRole?.locations.some(
          (location: Location) =>
            location.locationCode ===
            (agency as Location).locationKey.toString()
        )
      ) as Location[];
    }
    return locations;
  }

  public allData: () => Observable<GridDataResult> =
    (): Observable<GridDataResult> => {
      // TODO: Not sure how to change. Investigate replacing index signature with Record<string, IReferenceData[]>
      const nameTypeGrouping: Record<string, IReferenceData[]> = {};

      this.columnDefinitions
        .filter(
          (col: ColumnComponentInputs) => !!col.referenceDataItems?.length
        )
        .forEach((col: ColumnComponentInputs) => {
          nameTypeGrouping[col.field] = col.referenceDataItems;
        });

      return this.queryGridService({
        ...this.state,
        skip: 0,
        take: this.gridData.dataSourceResult.total,
      }).pipe(
        map((response: GridDataResponse) => {
          // don't do this for others (unless it becomes needed, then remove this check)
          if (!this.isEi) {
            return {
              data: response.dataSourceResult.data,
              total: response.dataSourceResult.total,
            };
          }

          // loop through all data, map IDs to DESCRIPTIONS for all reference data items
          // TODO: Not sure of the datatype. Define and use a proper datatype instead of any
          const data = response.dataSourceResult.data.map((item) => {
            const overrides = {};
            Object.keys(nameTypeGrouping).forEach((key) => {
              const mapped: string = getReferenceDataById(
                item[key],
                nameTypeGrouping[key]
              )?.description;
              if (mapped) {
                overrides[key] = mapped;
              }
            });
            return {
              ...item,
              ...overrides,
            };
          });

          return {
            data: data,
            total: response.dataSourceResult.total,
          };
        })
      );
    };

  onValueChange(): void {
    this.sendRequest();
  }

  resetGrid(): void {
    this.state = this.initialState;
    this.sendRequest();
  }

  onDataStateChangeBase(event: DataStateChangeEvent): void {
    this.state = event;
    this.sendRequest();
  }

  refreshGrid(): void {
    this.sendRequest();
  }

  reloadGrid(state: State): void {
    this.state = state;
    this.sendRequest();
  }

  closeRowsAndSendRequest(): void {
    this.sendRequest();
  }

  sendRequest(): void {
    // TODO: Not sure of the datatype. Define and use a proper datatype instead of any
    this.gridData?.dataSourceResult.data.forEach((idx) => {
      this.grid.collapseRow(idx);
    });
    this.loading = true;
    if (
      this.authStore.UserInfo?.selectedLocation &&
      this.authStore.UserInfo?.selectedRole
    ) {
      this.queryGridService()
        // eslint-disable-next-line @typescript-eslint/no-magic-numbers
        ?.pipe(take(1))
        .subscribe((response: GridDataResponse) => {
          if (this.gridApiRoute === EVENT_LOG) {
            this.gridData = response;
            this.loading = false;
          } else {
            if (
              !this.user?.permissions.some(
                (x: Permission) => x?.permissionId === this.permissionToView
              )
            ) {
              response.dataSourceResult = { data: [], total: 0 };
            }

            this.gridData = response;
            this.loading = false;
          }
          for (const key in this.gridData.distinctFieldValues) {
            // TODO: Not sure of the datatype. Define and use a proper datatype instead of any
            let distinctValues = this.gridData.distinctFieldValues[key].sort();
            const col: ColumnComponentInputs = this.columnDefinitions.find(
              (column: ColumnComponentInputs) => column.field === key
            );
            if (col.referenceDataItems?.length) {
              // TODO: Not sure of the datatype. Define and use a proper datatype instead of any
              const mappedData = distinctValues.map((value) => {
                // use == instead of === because the data comes back as a string sometimes vs as a number
                return col.referenceDataItems.find((item) => item.id == value);
              });
              distinctValues = orderBy(mappedData, 'sortOrder');
            }
            this.gridData.distinctFieldValues[key] = distinctValues;
          }
        });
    } else {
      this.gridData = {
        dataSourceResult: { data: [], total: 0 },
        distinctFieldValues: {},
        location: null,
      };
      this.loading = false;
    }
  }

  queryGridService(newState?: State): Observable<GridDataResponse> {
    return this.gridService.query(
      this.gridApiRoute,
      newState ? newState : this.state,
      this.columnDefinitions
        .filter(
          (col: ColumnComponentInputs) => col.columnType === ColumnType.select
        )
        .map((col: ColumnComponentInputs) => col.field),
      this.gridApiRoute === EITRANSITION
        ? this.currentAgency?.locationKey?.toString()
        : (this.authStore.UserInfo?.selectedLocation?.locationCode as string),
      this.studentSk,
      this.user?.selectedRole?.roleId,
      this.user?.selectedRole?.roleName
    );
  }

  /**
   * function for nested grid
   * @param event Detail Event  emitter
   */
  // TODO: Not sure of the datatype. Define and use a proper datatype instead of any
  detailExpandGrid(event: any): void {
    if (this.gridNestedApiRoute) {
      const data: NestedGridRequest = {
        roleId: 0,
        CaseAction: '',
        DistinctFields: '',
        DataSourceRequest: '',
      };
      switch (this.gridApiRoute) {
        case MenuAction.Evaluations:
          data.CaseAction = event.dataItem.referralSk;
          data.roleId = this.user.selectedRole?.roleId;
          this.sendRequestNested(data);
          break;
        case MenuAction.EiTransition:
          this.sendEiNestedRequest(event.dataItem.studentSk);
          break;
      }
    }
  }

  /**
   *
   * @param data  conatin id on which we get nested grid data
   */
  sendRequestNested(data: NestedGridRequest): void {
    data.DistinctFields = this.expandedRowColumnDefinitions
      .filter(
        (column: ColumnComponentInputs) => column.columnType === ColumnType.text
      )
      .map((column: ColumnComponentInputs) => column.field);
    data.DataSourceRequest = this.nestedState;

    this.loading = true;
    this.queryNestedGridService(data)
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      ?.pipe(take(1))
      .subscribe((response: GridDataResponse) => {
        if (
          this.user?.selectedRole?.roleId === USER_ROLE_CSE_SCHOOL_PSYCHOLOGIST
        ) {
          this.nestedGridData = response.dataSourceResult.data;
          // Removed for demo
          //.filter((item: any) => item.evaluatorName === evaluatorName);
        } else {
          this.nestedGridData = response.dataSourceResult.data;
        }
        this.loading = false;
      });
  }

  /**
   *
   * @param data conatin id on which we get nested grid data
   * @returns api data
   */
  queryNestedGridService(
    data: NestedGridRequest
  ): Observable<GridDataResponse> {
    return this.gridService.nestedGridQuery(this.gridApiRoute, data);
  }

  sendEiNestedRequest(studentSk: string, refreshGrid: boolean = false): void {
    this.loading = true;
    this.gridService
      .nestedEIGridQuery(studentSk)
      // eslint-disable-next-line @typescript-eslint/no-magic-numbers
      ?.pipe(take(1))
      .subscribe((response: LFDocument[]) => {
        this.loading = false;
        // TODO: Not sure of the datatype. Define and use a proper datatype instead of any
        const documentsData = [];
        const groupedDocs = groupBy(
          orderBy(response, 'uploadDate', 'desc'),
          (d) => (d.otherDocType ? d.otherDocType : d.documentType)
        );
        keys(groupedDocs).forEach((key: string) => {
          const firstDoc: LFDocument = first(groupedDocs[key]);
          documentsData.push({
            ...firstDoc,
            actions: firstDoc.actions?.filter((a: string) => a !== RE_UPLOAD),
          });
        });
        this.nestedGridData[studentSk.toUpperCase()] = [...documentsData];
      });
    if (refreshGrid) {
      this.refreshGrid();
    }
  }

  getSelectFilterData(
    col: ColumnComponentInputs
  ): number[] | string[] | IReferenceData[] {
    // TODO: Not sure of the datatype. Define and use a proper datatype instead of any
    let distinctValues = this.gridData.distinctFieldValues[col.field].sort();

    if (col.referenceDataItems?.length) {
      // TODO: Not sure of the datatype. Define and use a proper datatype instead of any
      const mappedData = distinctValues.map((value) => {
        // use == instead of === because the data comes back as a string sometimes vs as a number
        return col.referenceDataItems.find((item) => item.id == value);
      });

      distinctValues = orderBy(mappedData, 'sortOrder');
    }

    return distinctValues;
  }

  // TODO: Not sure of the datatype. Define and use a proper datatype instead of any
  evaluationDataRoute(data: any): string {
    return get(
      find(ASSESSMENT_INFO, { title: trim(get(data, 'evaluationName')) }),
      'route'
    );
  }

  getDate(date: any) {
    if (date) {
      return new Date(date);
    } else {
      return '';
    }
  }

  // TODO: Not sure of the datatype. Define and use a proper datatype instead of any
  showExpandedRow(data: any): boolean {
    return data.expandedRow?.length > FIRST_INDEX;
  }

  showAddButton(): boolean {
    if (!this.addButtonText) {
      return false;
    }

    if (this.isEi()) {
      return some(
        this.userDetails?.selectedRole?.userPermissions,
        (x: Permission) => x.permissionId === Permissions.CanAddEIRecords
      );
    }
    if (this.addButtonText === LOG_EVENT) {
      return some(
        this.userDetails?.selectedRole?.userPermissions,
        (x: Permission) => x.permissionId === Permissions.CanLogOutreach
      );
    }
    return (
      some(
        this.userDetails?.selectedRole?.userPermissions,
        (x: Permission) => x.permissionId === Permissions.CanAddReferralRecords
      ) ||
      some(
        this.userDetails?.selectedRole?.userPermissions,
        (x: Permission) => x.permissionId === Permissions.CanAddIEPRecords
      ) ||
      some(
        this.userDetails?.selectedRole?.userPermissions,
        (x: Permission) =>
          x.permissionId === Permissions.CanAddPlacementsRecords
      ) ||
      some(
        this.userDetails?.selectedRole?.userPermissions,
        (x: Permission) => x.permissionId === Permissions.CanAddEIRecords
      ) ||
      some(
        this.userDetails?.selectedRole?.userPermissions,
        (x: Permission) =>
          x.permissionId === Permissions.CanAddEvaluationsRecords
      ) ||
      some(
        this.userDetails?.selectedRole?.userPermissions,
        (x: Permission) => x.permissionId === Permissions.CanAddCBSTRecords
      )
    );
  }

  isEi(): boolean {
    return this.gridApiRoute === EITRANSITION;
  }

  showEiNoAddText(): boolean {
    return (
      this.isEi() &&
      !this.addButtonText &&
      includes(DOHMH_ROLES, this.user?.selectedRole?.roleName)
    );
  }

  routeToStudentProfile(studentSk: string, nycId: string): void {
    if (nycId) {
      this.studentStore.initializeSelectedStudentId(nycId);
      this.router.navigate([
        this.routesConstantService.routes.studentInformationPage,
      ]);
    } else {
      this.router.navigate([
        this.routesConstantService.routes.eiChildPage,
        'view',
        studentSk,
      ]);
    }
  }

  viewDocument(documentRow: StudentDocument): void {
    if (documentRow.isDoHmhMigrated === true) {
      // Open the document link in a new tab
      window.open(documentRow.docLink, '_blank');
    } else {
      // Continue with the existing logic
      this.documentService
        .getDocument(documentRow.documentRef ?? documentRow.lfEntryID)
        // TODO:We need to check datatype for the api endpoints, once added we need to add the datatype to the subscribe method
        .subscribe((response) => {
          this.pdfAndImageService.view(
            response,
            documentRow.documentFormat ?? documentRow.docFormat
          );
        });
    }
  }

  // TODO: Not sure of the datatype. Define and use a proper datatype instead of any
  getAllConsentSk(data: any) {
    return {
      twelveMonthConsentSk: data?.twelveMonthConsentSk,
      consentSkParentallyPlaced: data?.consentSkParentallyPlaced,
      consentSkInitialProvisionalService:
        data?.consentSkInitialProvisionalService,
      consentSkDeferredPlacement: data?.consentSkDeferredPlacement,
      consentSkDEP1: data?.consentSkDEP1,
      consentSkC7P: data?.consentSkC7P,
      consentSkShareInformationParentallyPlaced:
        data?.consentSkShareInformationParentallyPlaced,
      consentSkParentNoticeofParentallyPlaced:
        data?.consentSkParentNoticeofParentallyPlaced,
      consentSkESY: data?.consentSkESY,
      parentWithdrawalSk: data?.parentWithdrawalSk,
      noticeOfTerminationSk: data?.noticeOfTerminationSk,
    };
  }

  // TODO: Not sure of the datatype. Define and use a proper datatype instead of any
  getAllOutreachSk(data: any) {
    return {
      placementWithdrawalSk: data?.placementWithdrawalSk,
      finalNoticeofInitialEvalWithdrawalSk:
        data?.finalNoticeofInitialEvalWithdrawalSk,
    };
  }

  getAgencyById(locationCode: string): string {
    return this.referenceData.agency.find(
      (agency: IReferenceData) =>
        (agency as Location).locationKey?.toString() === locationCode
    )?.description;
  }

  isDohmhRole(): boolean {
    return includes(
      DOHMH_ROLE_IDS,
      this.authStore.UserInfo?.selectedRole?.roleId
    );
  }

  ngOnDestroy(): void {
    this.ngUnsubscribe.next();
    this.ngUnsubscribe.complete();
  }
}
